import { Link } from "react-router-dom";
import { Facebook, Globe, Instagram, Linkedin, Music2 } from "lucide-react";
import { Logo } from "./logo";
import { MOMENTOS } from "@/content/moments";
import { SERVICIOS } from "@/content/services";
import { SITE, whatsappHref, FOOTER_DISCLAIMER, CTAS, REDES_ACTIVAS } from "@/content/site";
import { trackEvent } from "@/lib/api";

const ICONOS_RED: Record<string, typeof Linkedin> = {
  linkedin: Linkedin,
  facebook: Facebook,
  instagram: Instagram,
  tiktok: Music2,
};

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-hairline bg-parchment-surface/60" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Logo tam="lg" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-ink-muted">
              {SITE.tagline} Acompañamiento jurídico para personas, familias y negocios en {SITE.alcance}.
            </p>
            <p className="mt-3 max-w-xs text-[11px] font-bold uppercase tracking-[0.06em] text-azul" data-testid="footer-lema">
              {SITE.lema}
            </p>
            {REDES_ACTIVAS.length > 0 && (
              <div className="mt-6" data-testid="footer-redes-sociales">
                <p className="eyebrow mb-3">Síguenos</p>
                <div className="flex gap-3">
                  {REDES_ACTIVAS.map((r) => {
                    const Icono = ICONOS_RED[r.id] || Globe;
                    return (
                      <a
                        key={r.id}
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Tulevik Legal en ${r.label}`}
                        data-testid={`footer-red-${r.id}`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-hairline bg-white text-azul transition-colors duration-200 hover:bg-azul hover:text-white"
                        onClick={() => trackEvent("cta_click", { red: r.id })}
                      >
                        <Icono className="h-4 w-4" strokeWidth={1.8} />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
            <a
              href={whatsappHref()}
              data-testid="footer-whatsapp-link"
              className="mt-6 inline-flex text-sm font-medium text-azul underline-offset-4 hover:underline"
              {...(SITE.WHATSAPP_NUMERO ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              {CTAS.secundaria.label}
            </a>
          </div>

          <nav className="md:col-span-3" aria-label="Momentos de vida" data-testid="footer-nav-momentos">
            <p className="eyebrow mb-5">Momentos de vida</p>
            <ul className="space-y-2.5">
              {MOMENTOS.slice(0, 5).map((m) => (
                <li key={m.slug}>
                  <Link
                    to={`/momentos/${m.slug}`}
                    data-testid={`footer-link-momento-${m.slug}`}
                    className="text-sm text-ink-text transition-colors hover:text-azul"
                  >
                    {m.nombre}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/momentos" data-testid="footer-link-momentos-todos" className="text-sm font-medium text-azul hover:underline">
                  Ver los ocho momentos
                </Link>
              </li>
            </ul>
          </nav>

          <nav className="md:col-span-3" aria-label="Servicios" data-testid="footer-nav-servicios">
            <p className="eyebrow mb-5">Servicios</p>
            <ul className="space-y-2.5">
              {SERVICIOS.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/servicios/${s.slug}`}
                    data-testid={`footer-link-servicio-${s.slug}`}
                    className="text-sm text-ink-text transition-colors hover:text-azul"
                  >
                    {s.nombre}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="md:col-span-2" aria-label="El despacho" data-testid="footer-nav-firma">
            <p className="eyebrow mb-5">El despacho</p>
            <ul className="space-y-2.5">
              <li><Link to="/como-te-acompanamos" data-testid="footer-link-metodo" className="text-sm text-ink-text hover:text-azul">Cómo te acompañamos</Link></li>
              <li><Link to="/ruta-legal" data-testid="footer-link-ruta" className="text-sm text-ink-text hover:text-azul">Ruta legal</Link></li>
              <li><Link to="/orientador" data-testid="footer-link-orientador" className="text-sm text-ink-text hover:text-azul">Encuentra tu ruta</Link></li>
              <li><Link to="/atencion-prioritaria" data-testid="footer-link-prioritaria" className="text-sm text-ink-text hover:text-azul">Atención prioritaria</Link></li>
              <li><Link to="/guias" data-testid="footer-link-guias" className="text-sm text-ink-text hover:text-azul">Guías</Link></li>
              <li><Link to="/nosotros" data-testid="footer-link-nosotros" className="text-sm text-ink-text hover:text-azul">Conoce Tulevik</Link></li>
              <li><Link to="/contacto" data-testid="footer-link-contacto" className="text-sm text-ink-text hover:text-azul">Contacto</Link></li>
            </ul>
          </nav>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-hairline pt-8 text-xs text-ink-muted md:flex-row md:items-center md:justify-between">
          <p data-testid="footer-copyright">
            © {year} Tulevik Legal · {SITE.ubicacion}
          </p>
          <p className="max-w-md" data-testid="footer-disclaimer">{FOOTER_DISCLAIMER}</p>
          <div className="flex gap-5">
            <Link to="/privacidad" data-testid="footer-link-privacidad" className="hover:text-azul">Privacidad</Link>
            <Link to="/terminos-de-uso" data-testid="footer-link-terminos" className="hover:text-azul">Términos de uso</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
